import {AccessibilityInfo, Platform} from 'react-native';

import {ExitPopup} from '/component/base/ExitPopup';
import {ACTION_TYPES, EVENT_NAMES} from '/enum/tealium.enum';
import {CrashlyticsBackend} from '/util/error-tracking/crashlytics';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import {showCandidateSavedToast, showToast} from '/util/toast.util';
import {validateEmail, validateName} from '/util/validation.util';

const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
  eventName: EVENT_NAMES.profileUpdate,
});

export const handleBackButton = ({
  name,
  phoneNumber,
  email,
  navigation,
  showPopup,
  hidePopup,
  lastPaidRank,
}: any) => {
  if (name || phoneNumber || email) {
    showPopup(
      ExitPopup,
      {
        title: 'Are you sure you want to go back?',
        description: 'All unsaved changes will be lost.',
        alignButtonsAsColumn: true,
        handleCancelButton: hidePopup,
        handleDiscardButton: async () => {
          await hidePopup();
          sendEvent({
            userId: 'none',
            updated_fields: 'none',
            action: ACTION_TYPES.abandon,
            lastPaidRank: lastPaidRank,
          });
          navigation.goBack();
        },
        exitButton: hidePopup,
      },
      {avoidable: false, showFullModal: false},
    );
  } else {
    sendEvent({
      userId: 'none',
      updated_fields: 'none',
      action: ACTION_TYPES.abandon,
      lastPaidRank: lastPaidRank,
    });
    navigation.goBack();
  }
};

const saveCandidateProfileData = async ({
  userId,
  credentialsEmail,
  name,
  email,
  cleanedNumber,
  urlDetails,
  lastPaidRank,
}: any) => {
  return {
    coachId: userId,
    coachEmailAddress: credentialsEmail,
    clientName: name,
    clientEmailAddress: email,
    clientPhone: cleanedNumber,
    source: 'COACH_APP',
    reason: 'COACH_CANDIDATE',
    urlDetails: urlDetails,
    lastPaidRank: lastPaidRank,
  };
};

export const handleSave = async ({
  userId,
  credentialsEmail,
  name,
  email,
  phoneNumber,
  setFetchApi,
  setNameError,
  setPhoneNumberError,
  setEmailError,
  setSocialLinksError,
  saveCandidateProfile,
  socialLinkText,
  navigation,
  lastPaidRank,
}: any) => {
  const urlDetails = [];
  const cleanedNumber = phoneNumber.replace(/\D/g, '');
  setFetchApi(true);

  const isNameValid = !validateName(name);
  setNameError(!isNameValid);

  const isPhoneNumberValid = cleanedNumber.length === 10;
  setPhoneNumberError(!isPhoneNumberValid);

  const isEmailValid = validateEmail(email);
  setEmailError(!isEmailValid);

  const pattern = /^((http|https|Https|Http|ftp|Ftp):\/\/)/;
  // social link url validation
  let hasSocialLinksError = false;

  for (let pair of socialLinkText) {
    const [key, value] = pair || [];

    setSocialLinksError(false);
    if (
      !value ||
      (key && !value.url) ||
      !pattern.test(value?.url?.toString())
    ) {
      setSocialLinksError(true);
      hasSocialLinksError = true;
      break;
    }
    urlDetails.push(value);
  }
  // field validation .......
  if (
    !isNameValid ||
    !isPhoneNumberValid ||
    !isEmailValid ||
    hasSocialLinksError
  ) {
    setFetchApi(false);
    return;
  }
  try {
    // api request formation......
    const candidateProfileData = await saveCandidateProfileData({
      userId,
      credentialsEmail,
      name,
      email,
      cleanedNumber,
      urlDetails,
      lastPaidRank,
    });
    //  api call.....
    await saveCandidateProfile(
      userId,
      candidateProfileData,
      sendEvent,
      lastPaidRank,
    );

    // If we reach here, the API call was successful
    showCandidateSavedToast();
    if (Platform.OS === 'ios') {
      await AccessibilityInfo.announceForAccessibility(
        'Candidate saved successfully',
      );
    }
    setTimeout(() => {
      navigation.goBack();
    }, 1000);
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'An error occurred while saving candidate';
    showToast('error', 'Candidate not saved successfully');
    await AccessibilityInfo.announceForAccessibility(
      'Candidate not saved successfully',
    );
    const crashlytics = new CrashlyticsBackend();
    crashlytics.logError(errorMessage);
  } finally {
    setFetchApi(false);
  }
};
